// Location discovery and map-visibility status (Phase 2). Per
// GAME_CREATION_INSTRUCTIONS.md's Phase 2 spec: connections should only
// appear once discovered or unlocked, and each location shows one of eight
// states (Unknown, Discovered but unavailable, Available, Current location,
// Under attack, Destroyed, Controlled by enemy, Controlled by player).

import type { Location, LoopState, PermanentState } from "../types"
import { getFactionById, routes } from "./content"
import { isRouteOpen } from "./travelEngine"

export type LocationStatus =
  | "unknown"
  | "discoveredUnavailable"
  | "available"
  | "current"
  | "underAttack"
  | "destroyed"
  | "controlledByEnemy"
  | "controlledByPlayer"

function neighborsOf(locationId: string, permanent: PermanentState): string[] {
  return routes
    .filter(
      (route) =>
        isRouteOpen(route, permanent) &&
        (route.fromLocationId === locationId || route.toLocationId === locationId),
    )
    .map((route) => (route.fromLocationId === locationId ? route.toLocationId : route.fromLocationId))
}

export function discoverLocation(
  permanent: PermanentState,
  locationId: string,
): PermanentState {
  if (permanent.discoveredLocations.includes(locationId)) return permanent
  return {
    ...permanent,
    discoveredLocations: [...permanent.discoveredLocations, locationId],
  }
}

/**
 * Arriving somewhere reveals it permanently, and reveals its direct
 * neighbors too — so their markers and outgoing connections start showing
 * on the map even before the player has actually traveled there.
 */
export function discoverLocationAndNeighbors(
  permanent: PermanentState,
  locationId: string,
): PermanentState {
  const withLocation = discoverLocation(permanent, locationId)
  return neighborsOf(locationId, withLocation).reduce(discoverLocation, withLocation)
}

/**
 * Reveals the far end of every *learnable* road (one that started locked and
 * carries an `unlock`) the player has now opened, spreading outward from
 * wherever they have already been. Learning a road should put its destination
 * on the map without the player having to walk there blind first — otherwise
 * the road is open but its endpoint is still "unknown", and there is no way to
 * set out for it.
 *
 * Deliberately follows only unlock-carrying routes, not the always-open ones:
 * the ordinary heartland is still uncovered by walking it (arrival reveals
 * immediate neighbours), so the map keeps its fog rather than laying the whole
 * open road network bare from Day 1. Discovery is permanent and only grows,
 * exactly like the knowledge that drives it.
 */
export function discoverReachableLocations(permanent: PermanentState): PermanentState {
  const known = new Set(permanent.discoveredLocations)
  let changed = true
  while (changed) {
    changed = false
    for (const route of routes) {
      if (!route.unlock || !isRouteOpen(route, permanent)) continue
      const { fromLocationId: a, toLocationId: b } = route
      if (known.has(a) !== known.has(b)) {
        known.add(a)
        known.add(b)
        changed = true
      }
    }
  }
  if (known.size === permanent.discoveredLocations.length) return permanent
  return { ...permanent, discoveredLocations: [...known] }
}

export function discoverRoute(permanent: PermanentState, routeId: string): PermanentState {
  if (permanent.discoveredRoutes.includes(routeId)) return permanent
  return { ...permanent, discoveredRoutes: [...permanent.discoveredRoutes, routeId] }
}

// Dawnmere's "burned" flag predates the generic "${locationId}_destroyed"
// naming convention newer content uses; kept as a one-off alias rather than
// renamed, to avoid touching already-verified Phase 1 event/dialogue wiring.
const DESTROYED_FLAG_OVERRIDES: Record<string, string> = {
  dawnmere: "dawnmere_burned",
}

function isDirectlyReachable(
  location: Location,
  permanent: PermanentState,
  loop: LoopState,
): boolean {
  return routes.some(
    (route) =>
      isRouteOpen(route, permanent) &&
      ((route.fromLocationId === loop.currentLocationId &&
        route.toLocationId === location.id) ||
        (route.toLocationId === loop.currentLocationId &&
          route.fromLocationId === location.id)),
  )
}

export function getLocationStatus(
  location: Location,
  permanent: PermanentState,
  loop: LoopState,
): LocationStatus {
  if (location.id === loop.currentLocationId) return "current"

  // Gate everything else behind discovery first: an enemy stronghold's
  // allegiance (or any other flag-driven state) must never leak onto the
  // map before the player has actually found the place.
  if (!permanent.discoveredLocations.includes(location.id)) return "unknown"

  const destroyedFlag = DESTROYED_FLAG_OVERRIDES[location.id] ?? `${location.id}_destroyed`
  if (loop.worldFlags.includes(destroyedFlag)) return "destroyed"

  if (loop.worldFlags.includes(`${location.id}_controlled_by_player`)) {
    return "controlledByPlayer"
  }

  const faction = location.factionId ? getFactionById(location.factionId) : null
  if (
    loop.worldFlags.includes(`${location.id}_controlled_by_enemy`) ||
    (faction && !faction.allied)
  ) {
    return "controlledByEnemy"
  }

  // "Under attack" only holds until the threat is resolved one way or the
  // other; a successful defense sets "${id}_defended" to supersede it rather
  // than clearing the (append-only) under-attack flag.
  if (
    loop.worldFlags.includes(`${location.id}_under_attack`) &&
    !loop.worldFlags.includes(`${location.id}_defended`)
  ) {
    return "underAttack"
  }

  return isDirectlyReachable(location, permanent, loop) ? "available" : "discoveredUnavailable"
}
